import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useEventContext} from '../../../../platform/src/context/EventContext';
import {useLocaleContext} from '../../../../platform/src/context/LocaleContext';
import {ModalContextProvider} from '../../../../platform/src/context/ModalContext';
import * as Api from '../../../../api';
import {getSortedMembers} from '../../../../platform/src/helpers/members';
import createCssVariables from '../../../widgetRegistration/src/core/utils/createCssVariables';
import {getCssVarsModel} from '../../../widgetSchedule/src/css-vars-model';
import {CButton} from '../../../../platform/src/components/UI';
import MembersPageView from '../../../../platform/src/components/Members/MembersViews/MembersPageView';
import PageContainer from '../../../../platform/src/components/PageContainer';
import MembersPageFilters from '../../../../platform/src/components/Members/MembersComponents/MembersPageFilters';
import MembersCarouselView from '../../../../platform/src/components/Members/MembersViews/MembersCarouselView';
import { useWidgetContext } from '../../../../platform/src/context/WidgetContextProvider';

import './style.scss';

const DEFAULT_VIEW_TYPE = 'allMembers';

const WidgetView = ({widgetKey}) => {
    const {event} = useEventContext();
    const $locale = useLocaleContext();
    const {setWidgetSettings: setSettingsWidget} = useWidgetContext();
    const widgetRef = useRef(null);
    const [widgetSettings, setWidgetSettings] = useState(null);
    const [visibleMembers, setVisibleMembers] = useState([]);
    const [filteredMembers, setFilteredMembers] = useState([]);
    const [membersPerPage, setMembersPerPage] = useState(1);

    // Получить настройки виджета, если указан widgetKey при инициализации
    useEffect(() => {
        !!widgetKey && fetchWidgetSettings();
    }, [widgetKey]);

    // Слушатель настроек для превью в КП
    useEffect(() => {
        const messageHandler = (event) => {
            try {
                const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

                if (!data) return;

                if (data?.hasOwnProperty('widgetSettings')) {
                    setWidgetSettings(data.widgetSettings);
                    setSettingsWidget(data.widgetSettings);
                }
            } catch (error) {
                console.error(error);
            }
        };

        window.addEventListener('message', messageHandler);

        return () => {
            window.removeEventListener('message', messageHandler);
        }
    }, []);

    // Применение настроек виджета к виджету
    useEffect(() => {
        if (!widgetSettings) return;

        setMembersPerPage(widgetSettings?.membersPerPage);

        const model = getCssVarsModel(widgetSettings);

        Object.entries(model).forEach(([varName, value]) => {
            if (!!value) {
                document.documentElement.style.setProperty(varName, value);
            }
        });

        createCssVariables(document.documentElement, widgetSettings);

        if (!widgetSettings?.font) return;

        const link = document.createElement('link');
        const fontPath = widgetSettings.font.split(' ').join('');
        link.setAttribute('rel', 'stylesheet');
        link.setAttribute('type', 'text/css');
        link.setAttribute('href', `/files/fonts/${fontPath}/stylesheet.css`);
        document.head.appendChild(link);

        return () => {
            document.head.removeChild(link);
        };
    }, [widgetSettings]);

    const widgetMembers = useMemo(() => {
        const membersList = event.members.filter(({roles}) => {
            return roles.some(({id}) => widgetSettings?.roles.includes(id));
        });

        if (event.moduleEpisode.sortMembers) {
            return getSortedMembers(membersList, $locale, 'lastname', 'en');
        }

        return membersList;
    }, [widgetSettings?.roles]);

    useEffect(() => {
        setFilteredMembers(widgetMembers);
    }, [widgetMembers]);

    useEffect(() => {
        getMembersOnPage();
    }, [filteredMembers, membersPerPage]);

    async function fetchWidgetSettings() {
        try {
            const response = await Api.player.getWidgetMembers(widgetKey);
            !!response && setWidgetSettings(response);
        } catch (error) {
            console.error(error);
        }
    }

    function getMembersOnPage() {
        if (widgetSettings?.view !== DEFAULT_VIEW_TYPE || filteredMembers?.length <= membersPerPage) {
            setVisibleMembers(filteredMembers);

            return;
        }

        const membersList = filteredMembers.slice(0, membersPerPage);
        setVisibleMembers(membersList);
    }

    return (
        <ModalContextProvider showModals={!!widgetSettings ? widgetSettings.showPopup : true}>
            <PageContainer>
                <section
                    ref={widgetRef}
                    className="members-page"
                >
                    {
                        !!widgetSettings?.showTitle &&
                        <div className="members-page-header">
                                <h1 className="members-page-header__title">
                                    {$locale.getProperty(widgetSettings || {}, 'title') || $locale.get('member.label.page-title')}
                                </h1>
                        </div>
                    }
                    <div className="members-page-filters">
                        <MembersPageFilters
                            event={event}
                            initialMembers={widgetMembers}
                            onFilter={setFilteredMembers}
                            hasSearch={!!widgetSettings?.showSearch}
                            hasTagsFilter={!!widgetSettings?.showTagsFilter}
                            hasSignsList={!!widgetSettings?.showRolesFilter}
                            hasOrganizationFilter={!!widgetSettings?.showOrganizationFilter}
                        />
                    </div>
                    {
                        widgetSettings?.view === DEFAULT_VIEW_TYPE
                            ? <div className="members-page-list">
                                <MembersPageView
                                    event={event}
                                    members={visibleMembers}
                                    showTextToRight={!!widgetSettings?.showTextToRight}
                                    isWidget={true}
                                    settings={widgetSettings}
                                    showBigPhotos={!!widgetSettings?.showBigPhotos}
                                    showPhotoCentered={!!widgetSettings?.showPhotoCentered}
                                />
                            </div>
                            : <MembersCarouselView
                                event={event}
                                members={visibleMembers}
                                isWidget={true}
                                settings={widgetSettings}
                                showAutoplay={!!widgetSettings?.showAutoplay}
                                showArrow={!!widgetSettings?.showArrow}
                            />
                    }
                    {
                        (widgetSettings?.view === DEFAULT_VIEW_TYPE && filteredMembers?.length > membersPerPage) &&
                        <div className="members-page-button">
                            <CButton
                                size="lg"
                                theme={widgetSettings?.showMoreAccent ? 'accent' : 'default'}
                                label={$locale.get('label.show-more')}
                                onClick={() => setMembersPerPage((prev) => prev + widgetSettings?.membersPerPage)}
                            />
                        </div>
                    }
                </section>
            </PageContainer>
        </ModalContextProvider>
    );
};

export default WidgetView;
